/**
 * @Description 发起、通过审批时，选择结点/选择审批人的组件
 */
import { FC, useEffect, useMemo, useState } from 'react';
import { getApprovalCreatorConfig } from '@/common/api/expandStore/approveworkbench';
import { isArray, isNotEmptyAny, refactorSelection } from '@lhb/func';
import V2FormSelect from '@/common/components/Form/V2FormSelect/V2FormSelect';
import { employeeListPermission } from '@/common/api/employee';
import { ApprovalTypeValue } from '@/common/components/business/ExpandStore/ts-config';

/*
  创建审批时必须传入
  id
  type
  typeValue

  重新发起时必须传入
  id
  type
  typeValue
  isReboot
  approvalId

  通过审批时必须传入
  aprDetail
*/

interface Props {
  // 创建审批、重新发起审批时必须传入
  id?: number; // 审批主体id，特例：新增开店区域类型因为还没创建数据不需要传
  type?: number; // 审批类型
  typeValue?: number; // 审批子类型
  // 重新发起时必须传入
  isReboot?: boolean; // 是否重新发起
  approvalId?: number; // 审批id
  // 通过审批时必须传入
  aprDetail?: any; // 审批详情
  // 无数据时提示，默认不需要设置。地图上弹出表单框时，输入框搜索无结果情况下展示了英文NoData，需要自定义设置；
  notFoundContent?: any; // 无数据提示
  // 回调
  onHasConfig?: Function, // 回调是否有选人选节点配置
}

const ApprovalSelectNodes: FC<Props> = ({
  id,
  type,
  typeValue,
  isReboot = false,
  approvalId = 0,
  aprDetail,
  notFoundContent,
  onHasConfig,
}) => {
  const [aprConfig, setAprConfig] = useState<any>({}); // 审批配置
  const [employeeOptions, setEmployeeOptions] = useState<any>([]); // 指派人选项

  useEffect(() => {
    // 特殊例，新增区域审批场景下，在生成区域前需要获取审批配置
    if ((id && typeValue) || (typeValue === ApprovalTypeValue.ModelClusterCreate)) {
      getApprovalConfig();
    }
  }, [id, typeValue]);

  const getApprovalConfig = () => {
    setAprConfig({});
    const params = isReboot ? {
      id,
      type,
      typeValue,
      approvalId,
    } : {
      id,
      type,
      typeValue,
    };
    getApprovalCreatorConfig(params).then((res) => {
      isNotEmptyAny(res) && setAprConfig(res);

      const { selectOperatorNodeList, selectNodeList } = res;
      const hasConfig = !!(isArray(selectOperatorNodeList) && selectOperatorNodeList.length) || !!(isArray(selectNodeList) && selectNodeList.length);
      onHasConfig?.(hasConfig);
    });
  };

  useEffect(() => {
    getEmployeeOptions();
  }, []);

  /**
   * @description 获取可指派的团队人员
   */
  const getEmployeeOptions = async () => {
    const { objectList: data } = await employeeListPermission({});
    const arr = isArray(data) && data.length ? data.map((item) => ({
      ...item,
      name: `${item.name} ${item.mobile}`
    })) : [];
    setEmployeeOptions(arr);
  };

  const selectOperatorNodeList = useMemo(() => {
    if (isNotEmptyAny(aprConfig)) {
      return aprConfig.selectOperatorNodeList;
    } else if (isNotEmptyAny(aprDetail)) {
      return aprDetail.selectOperatorNodeList;
    } else {
      return [];
    }
  }, [aprConfig, aprDetail]);

  const selectNodeList = useMemo(() => {
    if (isNotEmptyAny(aprConfig)) {
      return aprConfig.selectNodeList;
    } else if (isNotEmptyAny(aprDetail)) {
      return aprDetail.selectNodeList;
    } else {
      return [];
    }
  }, [aprConfig, aprDetail]);

  return (
    <>
      {isArray(selectOperatorNodeList) && selectOperatorNodeList.length ? selectOperatorNodeList.map((itm: any, idx: number) => (<V2FormSelect
        key={idx}
        label={`请选择${itm.name}审批人`}
        name={['nodeOperators', itm.nodeCode]}
        options={refactorSelection(employeeOptions)}
        placeholder='请选择审批人'
        required={true}
        rules={[{ required: true, message: `请选择${itm.name}审批人` }]}
        config={{
          mode: 'multiple',
          maxTagCount: 'responsive',
          showSearch: true,
          ...(notFoundContent ? { notFoundContent } : {})
        }}
      />)) : <></>}
      {isArray(selectNodeList) && selectNodeList.length ? <V2FormSelect
        label='请选择流程链路'
        name='nextNodeCode'
        options={selectNodeList}
        placeholder='请选择流程链路'
        required={true}
        rules={[{ required: true, message: `请选择流程链路` }]}
        config={{
          fieldNames: {
            label: 'name',
            value: 'nodeCode'
          },
          showSearch: true,
          ...(notFoundContent ? { notFoundContent } : {})
        }}
      /> : <></>}
    </>
  );
};
export default ApprovalSelectNodes;
